import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import NewsCard from "@/components/base/NewsCard";
import { useSeo } from "@/hooks/useSeo";
import { newsArticles, newsCategories } from "@/mocks/news";

export default function NewsPage() {
  const [activeCat, setActiveCat] = useState("all");

  useSeo({
    title: "爆料资讯 — 拍摄花絮、幕后制作故事与口碑解读",
    description:
      "短剧爆料社资讯频道，整理短剧拍摄花絮、幕后制作故事与观众口碑解读，带你了解镜头背后的操作与真实制作过程。",
    keywords: "爆料短剧,拍摄花絮,幕后制作故事,剧集口碑评分,短剧资讯",
    path: "/news",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "短剧爆料社 爆料资讯",
      url: "https://beesfly.cn/news",
      description: "拍摄花絮、幕后制作故事与口碑解读合集。",
    },
  });

  const filtered = useMemo(
    () =>
      activeCat === "all"
        ? newsArticles
        : newsArticles.filter((article) => article.category === activeCat),
    [activeCat],
  );

  const sorted = [...filtered].sort((a, b) => b.heat - a.heat);
  const lead = sorted[0];
  const rest = sorted.slice(1);

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "爆料资讯" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
            爆料资讯
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            拍摄花絮、幕后制作故事与口碑解读。编辑部尽量把现场发生过的事写清楚，
            而不是只复述剧情。共收录 {newsArticles.length} 篇长文。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-wrap gap-2">
          {newsCategories.map((cat) => {
            const isActive = activeCat === cat.key;
            return (
              <button
                key={cat.key}
                type="button"
                onClick={() => setActiveCat(cat.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

        {lead && (
          <article className="group mt-8 grid grid-cols-1 gap-6 overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:grid-cols-2">
            <div className="aspect-[16/10] w-full overflow-hidden bg-background-200 lg:aspect-auto lg:h-full">
              <img
                src={lead.cover}
                alt={`${lead.title} 配图`}
                title={`${lead.title} 拍摄花絮`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
              />
            </div>
            <div className="flex flex-col justify-center p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-accent-900">
                  {lead.category}
                </span>
                <time>{lead.date}</time>
                <span>·</span>
                <span>{lead.author}</span>
              </div>
              <h2 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
                {lead.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{lead.excerpt}</p>
              <Link
                to={`/news/${lead.id}`}
                className="mt-6 inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                阅读全文
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>
        )}

        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((article) => (
            <NewsCard key={article.id} article={article} />
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}